import {
  AVAILABILITY,
  EMBEDDED_LIMITS,
  type Availability,
  type ProductVariant,
} from '@geoic/shared';
import { Checkbox, Input } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { EnumSelect, Repeater, emptyTranslatable } from '@/features/blocks/BlockFields.js';
import { newBlockId } from '@/features/blocks/block-catalog.js';
import { price as formatPrice } from '@/lib/format.js';

export const AVAILABILITY_LABELS: Record<Availability, string> = {
  in_stock: 'Sotuvda bor',
  on_order: 'Buyurtma asosida',
  out_of_stock: 'Sotuvda yo‘q',
};

/**
 * Komplektatsiyalar (TZ 3.9).
 *
 * Narx AYNAN shu yerda yashaydi — mahsulotda umumiy narx yo'q. Katalogdagi
 * `priceMin`/`priceMax` server tomonda shu ro'yxatdan hisoblanadi, shuning
 * uchun adminda ularni tahrirlash mumkin emas.
 */
export function VariantsEditor({
  value,
  onChange,
}: {
  value: ProductVariant[];
  onChange: (value: ProductVariant[]) => void;
}) {
  return (
    <Repeater
      label="Komplektatsiyalar"
      max={EMBEDDED_LIMITS.PRODUCT_VARIANTS}
      addLabel="Komplektatsiya"
      items={value}
      getId={(variant) => variant._id}
      summary={(variant, index) =>
        `${variant.title.uz || `${index + 1}-komplekt`} — ${formatPrice(variant.price)}`
      }
      create={() => ({
        _id: newBlockId(),
        title: emptyTranslatable(),
        sku: null,
        price: null,
        availability: 'on_order' as const,
        includes: emptyTranslatable(),
        image: null,
        order: value.length,
        isVisible: true,
      })}
      onChange={(variants) => onChange(variants.map((v, index) => ({ ...v, order: index })))}
      emptyHint="Kamida bitta komplektatsiya kerak — narx va mavjudlik shu yerdan olinadi."
      renderItem={(variant, update) => (
        <>
          <TranslatableInput
            label="Nomi"
            required
            value={variant.title}
            onChange={(title) => update({ ...variant, title })}
            hint="«Standard», «Fly More Combo», «Cine Premium»"
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="SKU (artikul)"
              value={variant.sku ?? ''}
              placeholder="MAVIC3P-FMC"
              onChange={(event) => update({ ...variant, sku: event.target.value || null })}
              hint="Narxlarni XLSX orqali import qilish SHU maydon bo‘yicha ishlaydi"
            />
            <Input
              type="number"
              label="Narx (so‘m)"
              min={0}
              value={variant.price ?? ''}
              placeholder="Bo‘sh — «Narxni so‘rang»"
              onChange={(event) =>
                update({
                  ...variant,
                  price: event.target.value === '' ? null : Number(event.target.value),
                })
              }
            />
          </div>

          <EnumSelect
            label="Mavjudligi"
            value={variant.availability}
            options={AVAILABILITY.map((value) => ({
              value,
              label: AVAILABILITY_LABELS[value],
            }))}
            onChange={(availability) => update({ ...variant, availability })}
          />

          <TranslatableInput
            label="Komplekt tarkibi"
            multiline
            rows={3}
            value={variant.includes}
            onChange={(includes) => update({ ...variant, includes })}
            hint="«Dron, pult, 3 ta batareya, sumka»"
          />

          <MediaPicker
            label="Rasm"
            value={variant.image}
            onChange={(image) => update({ ...variant, image })}
            hint="Bo‘sh qoldirilsa mahsulotning muqova rasmi ishlatiladi"
          />

          <Checkbox
            label="Saytda ko‘rsatilsin"
            checked={variant.isVisible}
            onChange={(event) => update({ ...variant, isVisible: event.target.checked })}
          />
        </>
      )}
    />
  );
}
